import type { ComponentProps } from 'react'
import { FieldInfo, hasFieldError, useFieldContext, useFormReadOnly, useIsFieldOptional } from './shared'
import { Input } from '../../ui/input'
import { FieldLabelRow } from './field-label-row'

export default function FileField({
  label,
  className,
  optional,
  ...props
}: ComponentProps<'input'> & { label: string; optional?: boolean }) {
  const field = useFieldContext<File | undefined>()
  const formReadOnly = useFormReadOnly()
  const isReadOnly = props.readOnly ?? formReadOnly
  const isOptional = useIsFieldOptional(field.name, optional)
  const value = field.state.value
  const fileName = value instanceof File ? value.name : ''

  return (
    <div className={className}>
      <div className="grid gap-2">
        {label && (
          <FieldLabelRow
            label={label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
          />
        )}
        {isReadOnly ? (
          <div className="text-muted-foreground flex h-8 items-center text-sm">{fileName || '-'}</div>
        ) : (
          <Input
            type="file"
            id={field.name}
            name={field.name}
            aria-invalid={hasFieldError(field.state.meta)}
            ref={(ref) => {
              if (!ref) return
              if (!field.state.value) return
              if (!(field.state.value instanceof File)) return

              // Create FileList so we can restore persisted file on input
              const dataTransfer = new DataTransfer()
              dataTransfer.items.add(field.state.value)
              ref.files = dataTransfer.files
            }}
            onChange={(event) => {
              const file = event.target.files?.[0]

              field.handleChange(file)
              if (!file) return
            }}
            {...props}
          />
        )}
      </div>
      <FieldInfo fieldState={field.state} />
    </div>
  )
}
